import { memo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Spinner } from 'react-bootstrap';

import Icon from '../Icon';

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(21, 78, 170, 0.7);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 0 0 8px rgba(21, 78, 170, 0);
  }
`;

const ButtonContainer = styled.button`
  padding: 18px ${(props) => (props.withContent ? '24' : '18')}px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  height: ${(props) => (props.noPadding ? '0px' : props.flexibleHeight ? 'auto' : '56px')};

  ${(props) => (props.fullWidth ? 'width: 100%;' : '')};
  ${(props) => (props.disabled ? 'opacity: 0.7;' : '')};
  ${(props) => (props.disabled ? 'cursor: not-allowed;' : '')};
  ${(props) => (props.isTextCentered ? 'justify-content: center; align-items: center;' : '')};

  ${(props) =>
    props.size === 'sm' ? 'padding: 6px 10px;' : props.size === 'md' ? 'padding: 12px 14px;' : ''};
  ${(props) =>
    props.size === 'sm' ? 'height: 26px;' : props.size === 'md' ? 'height: 46px;' : ''};

  ${(props) => (props.noPadding ? 'padding: 0px;' : '')};

  background: ${(props) => {
    switch (props.variant) {
      case 'highlight':
        return 'linear-gradient(135deg, #154eaa 0%, #531dd8 100%)';
      case 'primary':
        return 'var(--blue-100);';
      case 'secondary':
        return 'var(--white);';
      case 'danger':
        return 'var(--red-50);';
      case 'text':
        return 'transparent;';
      default:
        return 'inherit';
    }
  }};

  color: ${(props) => {
    switch (props.variant) {
      case 'highlight':
        return 'var(--white);';
      case 'primary':
        return 'var(--white);';
      case 'danger':
        return 'var(--white);';
      case 'secondary':
      case 'text':
        return 'var(--blue-100);';
      default:
        return 'inherit';
    }
  }};

  border: ${(props) => {
    switch (props.variant) {
      case 'highlight':
      case 'primary':
      case 'danger':
      case 'text':
        return '0;';
      case 'secondary':
        return '0.9px solid var(--grey-50);';
      default:
        return 'inherit';
    }
  }};

  ${(props) => (props.isPulsing ? css`animation: ${pulse} 2s ease-in-out infinite;` : '')};

  &:hover {
    background: ${(props) => {
      switch (props.variant) {
        case 'highlight':
          return 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);';
        case 'primary':
          return 'var(--blue-80);';
        case 'danger':
          return 'var(--red-30);';
        case 'secondary':
          return 'var(--white);';
        case 'text':
          return 'var(--grey-30);';
        default:
          return 'inherit';
      }
    }};

    border: ${(props) => {
      switch (props.variant) {
        case 'secondary':
          return '0.9px solid var(--blue-100);';
        case 'highlight':
        case 'primary':
        case 'danger':
        case 'text':
          return '0;';
        default:
          return 'inherit';
      }
    }};

    color: ${(props) => {
      switch (props.variant) {
        case 'highlight':
          return 'var(--white);';
        case 'primary':
          return 'var(--white);';
        case 'danger':
          return 'var(--white);';
        default:
          return 'var(--blue-100);';
      }
    }};
  }
`;

const IconWrapper = styled.span`
  margin-right: ${(props) => {
    if (props.withContent) {
      return '8px';
    } else {
      return '0;';
    }
  }};
  width: 20px;
  height: 20px;
  overflow: hidden;
  color: inherit;
`;

const ContentWrapper = styled.span`
  text-align: center;
  leading-trim: both;

  text-edge: cap;
  font-feature-settings:
    'clig' off,
    'liga' off;
  font-family: 'Plus Jakarta Sans';
  font-size: ${(props) => {
    if (props.fontSize) return props.fontSize;
    if (props.size === 'sm') return '13px';
    return '18px';
  }};
  font-style: normal;
  font-weight: 500;
  line-height: ${(props) => (props.maxNoOfLines ? '1.4' : '110%')};

  ${(props) => {
    if (props.maxNoOfLines) {
      return `
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: ${props.maxNoOfLines};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        word-break: break-word;
      `;
    }
    return `text-wrap: ${props.flexibleHeight ? 'wrap' : 'nowrap'};`;
  }};

  ${(props) => (props.fullWidth ? 'width: 100%;' : '')};

  @media (max-width: 768px) {
    font-size: ${(props) => {
      if (props.fontSizeMobile) return props.fontSizeMobile;
      if (props.fontSize) return props.fontSize;
      return '16px';
    }};
  }
`;

const Button = ({
  onClick,
  children,
  iconName,
  variant = 'primary',
  fullWidth = false,
  flexibleHeight = false,
  type,
  disabled,
  loading,
  ref,
  iconColor,
  iconSize,
  noPadding = false,
  size = 'lg',
  isTextCentered = false,
  isPulsing = false,
  maxNoOfLines,
  className,
  fontSize,
  fontSizeMobile,
  ['data-qa']: dataQa
}) => {
  return (
    <ButtonContainer
      variant={variant}
      onClick={onClick}
      iconName={iconName}
      withContent={!!children}
      fullWidth={fullWidth}
      flexibleHeight={flexibleHeight}
      type={type}
      disabled={loading || disabled}
      ref={ref}
      noPadding={noPadding}
      size={size}
      isTextCentered={isTextCentered}
      isPulsing={isPulsing}
      className={className}
      data-qa={dataQa}
    >
      {iconName ? (
        <IconWrapper withContent={!!children}>
          {children ? (
            <Icon
              name={iconName}
              size={iconSize}
              color={iconColor ? iconColor : variant === 'primary' ? 'white' : ''}
            />
          ) : loading ? (
            <Spinner
              animation="border"
              variant={variant === 'secondary' || variant === 'text' ? 'primary' : 'white'}
              size="sm"
              color={variant === 'secondary' || variant === 'text' ? 'primary' : 'white'}
            />
          ) : (
            <Icon
              name={iconName}
              size={iconSize}
              color={iconColor ? iconColor : variant === 'primary' ? 'white' : ''}
            />
          )}
        </IconWrapper>
      ) : null}
      {children ? (
        <ContentWrapper
          fullWidth={fullWidth}
          size={size}
          data-qa={`${dataQa}_text`}
          flexibleHeight={flexibleHeight}
          maxNoOfLines={maxNoOfLines}
          fontSize={fontSize}
          fontSizeMobile={fontSizeMobile}
        >
          {children}
          {loading ? (
            <>
              {' '}
              <Spinner
                animation="border"
                variant={variant === 'secondary' ? 'primary' : 'white'}
                size="sm"
                color={variant === 'secondary' ? 'primary' : 'white'}
              />
            </>
          ) : null}
        </ContentWrapper>
      ) : null}
    </ButtonContainer>
  );
};

export default memo(Button);
